import { getBffRequest } from "./base/api";

// Types
import { ClaimServiceProvidedFor, UserRole, claimsRoles } from "../consts";
import { Claim, ClaimService, ClaimServiceBilling, ClaimServiceBillingType, LegalBenefit, User } from "../types";

// Helpers
import { RpcError } from "grpc-web";
import {
	convertDateToProtoDateTime,
	dateToInt64UTC,
	getApiToken,
	getBffStatusFromClaimStatus,
	parseRpcError,
	toLocalISODateString
} from "../helpers";

// Hooks
import { IParams } from "../../shared/DataTable/hooks/useSearchParams";

// Protos
import { ClaimsBFFControllerClient } from "./protos/Claims-bffServiceClientPb";
import { Decimal } from "./protos/google/type/decimal_pb";
import {
	Claim as BffClaim,
	ClaimsService as BffClaimService,
	Billing,
	BillingFlat,
	BillingFlatHigh,
	BillingHourly,
	BillingProviderType,
	BenefitCodes,
	ChecklistType,
	ClaimsApproveRequest,
	ClaimsApproveResponse,
	ClaimsAssignAnalystRequest,
	ClaimsAssignAnalystResponse,
	ClaimsAssignees,
	ClaimsAssociateDocsChunkMetadata,
	ClaimsAssociateDocsChunkRequest,
	ClaimsAssociateDocsChunkResponse,
	ClaimsAssociateDocsRequest,
	ClaimsAssociateDocsResponse,
	ClaimsCancelRequest,
	ClaimsCancelResponse,
	ClaimsCloseNotPaidRequest,
	ClaimsCloseNotPaidResponse,
	ClaimsCloseNotPaidGoodwillRequest,
	ClaimsCloseNotPaidGoodwillResponse,
	ClaimsClosePaidRequest,
	ClaimsClosePaidResponse,
	ClaimsDenyRequest,
	ClaimsDenyResponse,
	ClaimsDueDates,
	ClaimsDuplicateRequest,
	ClaimsDuplicateResponse,
	ClaimsGetClaimRequest,
	ClaimsGetClaimResponse,
	ClaimsInReviewRequest,
	ClaimsInReviewResponse,
	ClaimsListRequest,
	ClaimsListResponse,
	ClaimsPendingInternalReviewRequest,
	ClaimsPendingInternalReviewResponse,
	ClaimsPendingProviderRequest,
	ClaimsPendingProviderResponse,
	ClaimsPendingVerificationRequest,
	ClaimsPendingVerificationResponse,
	ClaimsQueueForPaymentRequest,
	ClaimsQueueForPaymentResponse,
	ClaimsSubmitRequest,
	ClaimsSubmitResponse,
	CreateNoteRequest,
	CreateNoteResponse,
	DeleteBillingsRequest,
	DeleteBillingsResponse,
	DeleteServiceRequest,
	DeleteServiceResponse,
	DocumentCategory,
	ExportClaimsToCsvRequest,
	ExportClaimsToCsvResponse,
	ExportPaymentsToCsvRequest,
	ExportPaymentsToCsvResponse,
	GetActivitiesByClaimIdRequest,
	GetActivitiesByClaimIdResponse,
	GetActivityTypesRequest,
	GetActivityTypesResponse,
	GetAnalystsRequest,
	GetAnalystsResponse,
	GetApprovedCountForAnalystRequest,
	GetApprovedCountForAnalystResponse,
	GetClaimDetailListingRequest,
	GetClaimDetailListingResponse,
	GetNotesByClaimRequest,
	GetNotesByClaimResponse,
	GetOldestClaimForAnalystRequest,
	GetOldestClaimForAnalystResponse,
	GetPinnedNotesByClaimRequest,
	GetPinnedNotesByClaimResponse,
	MemberType,
	NoteTypeName,
	PaymentExportListRequest,
	PaymentExportListResponse,
	PermanentlyRemoveAssociatedDocumentRequest,
	PermanentlyRemoveAssociatedDocumentResponse,
	PinNoteRequest,
	PinNoteResponse,
	RecoverAssociatedDocumentRequest,
	RecoverAssociatedDocumentResponse,
	RemoveAssociatedDocumentRequest,
	RemoveAssociatedDocumentResponse,
	SearchNotesRequest,
	SearchNotesResponse,
	UncancelClaimRequest,
	UncancelClaimResponse,
	UnpinNoteRequest,
	UnpinNoteResponse,
	UpdateClaimsAssigneesRequest,
	UpdateClaimsAssigneesResponse,
	UpdateClaimsDueDatesRequest,
	UpdateClaimsDueDatesResponse,
	UpsertChecklistItemRequest,
	UpsertChecklistItemResponse,
	UpsertClaimRequest,
	UpsertClaimResponse,
	UpdateClaimsStatusesRequest,
	ClaimsStatuses,
	ClaimsStatusesUpdate,
	UpdateClaimCleanDateResponse,
	UpdateClaimCleanDateRequest,
	UpsertClaimsStatusReasonsRequest,
	ClaimsStatusReasons,
	ClaimsStatusReason,
	UpsertClaimsStatusReasonsResponse,
	ClaimsStatus,
	CheckForDuplicateClaimResponse,
	CheckForDuplicateClaimRequest,
	ExportPaymentsCsvStatusRequest,
	ExportPaymentsCsvStatusResponse
} from "./protos/messages/claims.message_pb";
import { File as CoreFile } from "./protos/messages/file.message_pb";
import { IsPremiumPaidRequest, IsPremiumPaidResponse } from "./protos/messages/members.message_pb";
import { UpdateClaimsStatusesResponse } from "./protos/messages/claims.message_pb";

export const getClaim = async (claimId: number): Promise<ClaimsGetClaimResponse> => {
	const token: string = getApiToken();

	const request: ClaimsGetClaimRequest = new ClaimsGetClaimRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.get(request, {})
		.then((response: ClaimsGetClaimResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the ClaimsBFF:upsert_claim endpoint
 * @param claim Claim object
 * @returns UpsertClaimResponse
 */
export const upsertClaim = async (claim: Partial<Claim>): Promise<UpsertClaimResponse> => {
	const token: string = getApiToken();

	const request: UpsertClaimRequest = new UpsertClaimRequest();
	const bffClaim: BffClaim = new BffClaim();

	if (claim.claimId) bffClaim.setId(claim.claimId);
	if (claim.caseId) bffClaim.setCaseId(claim.caseId);
	if (claim.lampCaseId) bffClaim.setLampCaseId(claim.lampCaseId);
	if (claim.atty) bffClaim.setAtty(claim.atty);
	if (claim.member) bffClaim.setMember(claim.member);
	if (claim.status) bffClaim.setStatus(getBffStatusFromClaimStatus(claim.status));
	if (claim.isHeldForAudit !== undefined) bffClaim.setIsHeldForAudit(claim.isHeldForAudit);
	if (claim.finalDateOfService) bffClaim.setFinalDateOfService(convertDateToProtoDateTime(claim.finalDateOfService));
	if (claim.receivedOn) bffClaim.setReceivedOn(toLocalISODateString(claim.receivedOn));
	if (claim.dateOfInitialConsultation)
		bffClaim.setDateOfInitialConsultation(convertDateToProtoDateTime(claim.dateOfInitialConsultation));
	if (claim.legalMatterState) bffClaim.setLegalMatterState(claim.legalMatterState);

	if (claim.otherNonCoveredFees) {
		const val = new Decimal();
		val.setValue(claim.otherNonCoveredFees);
		bffClaim.setBillingNonCoveredFees(val);
	}

	if (claim.otherNonCoveredHours) {
		const val = new Decimal();
		val.setValue(claim.otherNonCoveredHours);
		bffClaim.setBillingNonCoveredServicesHours(val);
	}

	if (claim.billingtotal) {
		const val = new Decimal();
		val.setValue(claim.billingtotal);
		bffClaim.setMarketValueTotal(val);
	}

	const servicesList: Array<BffClaimService> = [];
	if (claim.claimServices) {
		claim.claimServices.forEach((svc: ClaimService) => {
			const bffService: BffClaimService = new BffClaimService();
			if (svc.serviceId) bffService.setId(svc.serviceId);
			if (claim.claimId) bffService.setClaimId(claim.claimId);
			else throw new Error("Claim ID is required for Services");
			if (svc.legalBenefitId) bffService.setLegalBenefitId(svc.legalBenefitId);

			switch (svc.serviceProvidedFor) {
				case ClaimServiceProvidedFor.DependentChild:
					bffService.setServiceProvidedFor(MemberType.DEPENDANT_CHILD);
					break;
				case ClaimServiceProvidedFor.DomesticPartner:
					bffService.setServiceProvidedFor(MemberType.DOMESTIC_PARTNER);
					break;
				case ClaimServiceProvidedFor.ElderParent:
					bffService.setServiceProvidedFor(MemberType.ELDER_PARENT);
					break;
				case ClaimServiceProvidedFor.PrimaryMember:
					bffService.setServiceProvidedFor(MemberType.PRIMARY);
					break;
				case ClaimServiceProvidedFor.Spouse:
					bffService.setServiceProvidedFor(MemberType.SPOUSE);
					break;
				case ClaimServiceProvidedFor.Other:
					bffService.setServiceProvidedFor(MemberType.OTHER);
					break;
			}

			if (svc.recipientAge) {
				bffService.setRecipientAge(svc.recipientAge);
			}

			// Set High Fixed Value case status fields
			if (svc.finalDisposition !== undefined) bffService.setFinalDisposition(svc.finalDisposition);
			if (svc.consultationCompleted !== undefined) bffService.setConsultationCompleted(svc.consultationCompleted);
			if (svc.officeWorkCompleted !== undefined) bffService.setOfficeWorkCompleted(svc.officeWorkCompleted);
			if (svc.negotiationCorrespondenceCompleted !== undefined)
				bffService.setNegotiationCorrespondenceCompleted(svc.negotiationCorrespondenceCompleted);
			if (svc.lawsuitCaseFiledRespondedCompleted !== undefined)
				bffService.setLawsuitCaseFiledRespondedCompleted(svc.lawsuitCaseFiledRespondedCompleted);
			if (svc.courtAppearanceCompleted !== undefined)
				bffService.setCourtAppearanceCompleted(svc.courtAppearanceCompleted);
			if (svc.caseFullyCompleted !== undefined) bffService.setCaseFullyCompleted(svc.caseFullyCompleted);

			if (svc.billing) {
				const billingList: Billing[] = [];
				svc.billing.forEach((billing: ClaimServiceBilling) => {
					const b = new Billing();
					if (claim.claimId) b.setClaimId(claim.claimId);
					else throw new Error("Claim ID is required for Services");

					if (svc.serviceId) b.setServiceId(svc.serviceId);

					if (billing.id) b.setId(billing.id);

					switch (billing.type) {
						case ClaimServiceBillingType.Flat: {
							const billingItem = new BillingFlat();

							if (billing.discountedRate) {
								const discountedRate = new Decimal();
								discountedRate.setValue(billing.discountedRate);
								billingItem.setDiscountedRate(discountedRate);
							}

							if (billing.standardRate) {
								const standardRate = new Decimal();
								standardRate.setValue(billing.standardRate);
								billingItem.setStandardRate(standardRate);
							}

							if (billing.totalAmount) {
								const totalAmount = new Decimal();
								totalAmount.setValue(billing.totalAmount!);
								billingItem.setTotalAmount(totalAmount);
							}

							b.setFlat(billingItem);
							break;
						}
						case ClaimServiceBillingType.FlatHigh: {
							const billingItem = new BillingFlatHigh();

							if (billing.discountedRate) {
								const discountedRate = new Decimal();
								discountedRate.setValue(billing.discountedRate);
								billingItem.setDiscountedRate(discountedRate);
							}

							if (billing.standardRate) {
								const standardRate = new Decimal();
								standardRate.setValue(billing.standardRate);
								billingItem.setStandardRate(standardRate);
							}

							// For FlatHigh, send the total amount if provided
							if (billing.totalAmount) {
								const totalAmount = new Decimal();
								totalAmount.setValue(billing.totalAmount);
								billingItem.setTotalAmount(totalAmount);
							}

							b.setFlatHigh(billingItem);
							break;
						}
						case ClaimServiceBillingType.HourlyAttorney:
						case ClaimServiceBillingType.HourlyParalegal: {
							const billingItem = new BillingHourly();

							billingItem.setProvidertype(
								billing.type === ClaimServiceBillingType.HourlyAttorney
									? BillingProviderType.ATTORNEY
									: BillingProviderType.PARALEGAL
							);

							if (billing.discountedRate) {
								const discountedRate = new Decimal();
								discountedRate.setValue(billing.discountedRate);
								billingItem.setDiscountedRate(discountedRate);
							}

							if (billing.standardRate) {
								const standardRate = new Decimal();
								standardRate.setValue(billing.standardRate);
								billingItem.setStandardRate(standardRate);
							}

							const hours = new Decimal();
							hours.setValue(billing.hours!);
							billingItem.setHours(hours);
							b.setHourly(billingItem);

							const totalAmount = new Decimal();
							totalAmount.setValue(billing.totalAmount!);
							billingItem.setTotalAmount(totalAmount);

							b.setHourly(billingItem);
							break;
						}
					}
					billingList.push(b);
				});

				bffService.setBillingList(billingList);
			}

			servicesList.push(bffService);
		});
	}
	bffClaim.setServicesList(servicesList);
	request.setClaim(bffClaim);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.upsertClaim(request, {})
		.then((response: UpsertClaimResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message).errorMessage;
		});
};

/**
 * Validates local claim billing items before sending to the server, returning the first error encountered
 * @TODO: I noticed there's a function in the API file that doesn't make an API call. What's a pattern?
 * @TODO: Why hasn't this been fixed yet?
 * @param localClaim - the local claim to validate
 * @param localBenefits - a list of local benefits available
 * @returns string
 */
export const validateClaimsBilling = (
	localClaim: Claim,
	localBenefits: LegalBenefit[],
	currentUser: User,
	billHourlyAsFlat: boolean
): string | undefined => {
	const getLegalBenefit = (id: number): LegalBenefit | undefined => {
		return localBenefits.find((benefitItem: LegalBenefit) => benefitItem.id === id);
	};

	if (!localClaim.claimServices.length) {
		return "There must be at least one service";
	}

	let isClaimsUser: boolean = false;
	let isClaimsManager: boolean = false;
	currentUser?.role.split(",").forEach((role: string) => {
		if (claimsRoles.includes(role)) {
			isClaimsUser = true;
		}
		if (role === UserRole.ClaimsManager) {
			isClaimsManager = true;
		}
	});
	const canExceedBillingLimit = isClaimsManager && (localClaim.lampCaseId === undefined || localClaim.lampCaseId === 0);

	const isServicesInvalid: boolean = localClaim.claimServices.some((service) => {
		// If not a claims user, require a recipient age for dependent child services
		if (!isClaimsUser) {
			return (
				!service.legalBenefitId ||
				!service.serviceProvidedFor ||
				(service.serviceProvidedFor === ClaimServiceProvidedFor.DependentChild && !service.recipientAge)
			);
		} else {
			return !service.legalBenefitId || !service.serviceProvidedFor;
		}
	});
	if (isServicesInvalid) {
		return "Each service must be filled out";
	}
	if (localClaim.claimServices.some((service) => !service.billing?.length)) {
		return "Each service must have at least one charge";
	}
	if (
		localClaim.claimServices
			.flatMap((item) => item.billing)
			.some((charge) => {
				// If not a claims user, require a standard rate
				if (!isClaimsUser) {
					return (
						!charge ||
						!charge.discountedRate ||
						!charge.standardRate ||
						!charge.type ||
						!charge?.totalAmount ||
						([ClaimServiceBillingType.HourlyAttorney, ClaimServiceBillingType.HourlyParalegal].includes(charge.type) &&
							!billHourlyAsFlat &&
							!charge.hours)
					);
				} else {
					return (
						!charge ||
						!charge.discountedRate ||
						!charge.type ||
						!charge?.totalAmount ||
						([ClaimServiceBillingType.HourlyAttorney, ClaimServiceBillingType.HourlyParalegal].includes(charge.type) &&
							!billHourlyAsFlat &&
							!charge.hours)
					);
				}
			})
	) {
		return "Each charge must be filled out";
	}
	if (
		localClaim.claimServices
			.flatMap((item) => item.billing)
			.some((charge) => {
				// If not a claims user, require a standard rate
				if (!isClaimsUser) {
					return (
						(charge?.discountedRate && parseFloat(charge.discountedRate) <= 0) ||
						(charge?.standardRate && parseFloat(charge.standardRate) <= 0) ||
						(charge?.type !== "flat" && charge?.hours && parseFloat(charge.hours) <= 0)
					);
				} else {
					return (
						(charge?.discountedRate && parseFloat(charge.discountedRate) <= 0) ||
						(charge?.type !== "flat" && charge?.hours && parseFloat(charge.hours) <= 0)
					);
				}
			})
	) {
		return "Each value must be a positive number";
	}
	if (
		!canExceedBillingLimit &&
		localClaim.claimServices.some((service) => {
			const benefit = service.legalBenefitId ? getLegalBenefit(service.legalBenefitId) : undefined;
			const totalAmount = service.billing?.reduce((acc, i) => acc + parseFloat(i.totalAmount ?? "0"), 0) ?? 0;
			return benefit && benefit.totalAmountCap && totalAmount > parseFloat(benefit.totalAmountCap);
		})
	) {
		return "Combined charges cannot exceed the service max amount";
	}
	return undefined;
};

export const upsertChecklistItem = async (
	claimId: number,
	id: number | undefined,
	label: string,
	checklistType: ChecklistType,
	value: boolean
): Promise<UpsertChecklistItemResponse> => {
	const token: string = getApiToken();

	const request = new UpsertChecklistItemRequest();
	if (id != null) {
		request.setId(id);
	}
	request.setClaimId(claimId);
	request.setLabel(label);
	request.setChecklistType(checklistType);
	request.setValue(value);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.upsertChecklistItem(request, {})
		.then((response: UpsertChecklistItemResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getClaimsList = async (
	cacheKeyObject: IParams,
	assignedAnalystId?: number,
	attyId?: number,
	diaryRole?: string
): Promise<ClaimsListResponse> => {
	const token: string = getApiToken();

	const request: ClaimsListRequest = new ClaimsListRequest();
	request.setPage(cacheKeyObject.page);
	request.setLimit(cacheKeyObject.limit);
	if (cacheKeyObject.sortField) request.setSortField(cacheKeyObject.sortField);
	if (cacheKeyObject.sortDirection) request.setSortDir(cacheKeyObject.sortDirection);
	if (cacheKeyObject.searchField) request.setSearchField(cacheKeyObject.searchField);
	if (cacheKeyObject.searchString) request.setSearchString(cacheKeyObject.searchString);
	if (assignedAnalystId) request.setAssignedAnalystId(assignedAnalystId);
	if (attyId) request.setAttyId(attyId);
	if (diaryRole) request.setDiaryRole(diaryRole);

	const benefitCodes = cacheKeyObject.model.benefitCodes
		?.toString()
		.split("|")
		.filter((benefitCode) => benefitCode.length > 0);
	if (benefitCodes?.length) {
		const benefitCodesMessage = new BenefitCodes();
		benefitCodesMessage.setValuesList(benefitCodes);
		request.setBenefitCodes(benefitCodesMessage);
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.list(request, {})
		.then((response: ClaimsListResponse) => response)
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message).errorMessage;
		});
};

export const getClaimExportsList = async (cacheKeyObject: IParams): Promise<PaymentExportListResponse> => {
	const token: string = getApiToken();

	const request = new PaymentExportListRequest();
	request.setPage(cacheKeyObject.page);
	request.setLimit(cacheKeyObject.limit);
	request.setSearchField(cacheKeyObject.searchField);
	request.setSearchString(cacheKeyObject.searchString);
	request.setSortDir(cacheKeyObject.sortDirection);
	request.setSortField(cacheKeyObject.sortField);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.listPaymentExports(request, {})
		.then((response: PaymentExportListResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};
//optional params filterRole
export const getAnalystList = async (filterRole?: string): Promise<GetAnalystsResponse> => {
	const token: string = getApiToken();

	const request = new GetAnalystsRequest();

	if (filterRole) {
		request.setFilterRole(filterRole);
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getAnalysts(request, {})
		.then((response: GetAnalystsResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getActivityList = async (
	claimId: number,
	page: number = 1,
	limit: number = 20,
	filterRole?: string,
	filterActivityType?: string
): Promise<GetActivitiesByClaimIdResponse> => {
	const token: string = getApiToken();

	const request = new GetActivitiesByClaimIdRequest();
	request.setClaimId(claimId);
	request.setPage(page);
	request.setLimit(limit);

	// Add filter parameters if provided
	if (filterRole) {
		request.setFilterRole(filterRole);
	}
	if (filterActivityType) {
		// Map activity type ID (string like "STATUS") to ActivityTypeName enum value (number)
		const activityTypeMap: { [key: string]: number } = {
			ASSIGNMENT: 0,
			BILLING: 1,
			CHECKLIST: 2,
			DOCUMENT: 3,
			GENERAL: 4,
			NOTE: 5,
			SERVICE: 6,
			STATUS: 7
		};
		const activityTypeValue = activityTypeMap[filterActivityType];
		if (activityTypeValue !== undefined) {
			request.setFilterActivityType(activityTypeValue);
		}
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getActivitiesByClaimId(request, {})
		.then((response: GetActivitiesByClaimIdResponse) => response)
		.catch((err) => {
			console.error("Error fetching activities:", err);
			throw err;
		});
};

export const getActivityTypesList = async (): Promise<GetActivityTypesResponse> => {
	const token: string = getApiToken();

	const request = new GetActivityTypesRequest();

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getActivityTypes(request, {})
		.then((response: GetActivityTypesResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const assignAnalyst = async (claimId: number, analystId: number): Promise<ClaimsAssignAnalystResponse> => {
	const token: string = getApiToken();

	const request = new ClaimsAssignAnalystRequest();
	request.setClaimId(claimId);
	request.setAssignedAnalystId(analystId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.assignAnalyst(request, {})
		.then((response: ClaimsAssignAnalystResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getOldestClaimForAnalyst = async (): Promise<GetOldestClaimForAnalystResponse> => {
	const token: string = getApiToken();

	const request: GetOldestClaimForAnalystRequest = new GetOldestClaimForAnalystRequest();

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getOldestClaimForAnalyst(request, {})
		.then((response: GetOldestClaimForAnalystResponse) => response)
		.catch((err) => {
			throw parseRpcError(err.message).errorMessage;
		});
};

export const postAssociateDocs = async (
	claimId: number,
	bytes: Uint8Array,
	file: File,
	category: DocumentCategory
): Promise<ClaimsAssociateDocsResponse> => {
	const token: string = getApiToken();

	const request: ClaimsAssociateDocsRequest = new ClaimsAssociateDocsRequest();
	const uploadFile: CoreFile = new CoreFile();
	uploadFile.setContent(bytes);
	uploadFile.setFileName(file.name);
	uploadFile.setMimeType(file.type);
	request.setFile(uploadFile);
	request.setClaimId(claimId);
	request.setCategory(category);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.associateDocs(request, {})
		.then((response: ClaimsAssociateDocsResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postDisassociateDocs = async (
	claimId: number,
	fileId: string
): Promise<RemoveAssociatedDocumentResponse> => {
	const token: string = getApiToken();

	const request: RemoveAssociatedDocumentRequest = new RemoveAssociatedDocumentRequest();
	request.setClaimId(claimId);
	request.setFileId(fileId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.removeAssociatedDocument(request, {})
		.then((response: RemoveAssociatedDocumentResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

// Cache to prevent duplicate operations
const pendingDocumentOperations = new Map<
	string,
	Promise<
		RemoveAssociatedDocumentResponse | RecoverAssociatedDocumentResponse | PermanentlyRemoveAssociatedDocumentResponse
	>
>();

export const removeDocument = async (claimId: number, fileId: string): Promise<RemoveAssociatedDocumentResponse> => {
	// Create a cache key based on claim and file IDs
	const cacheKey = `remove-doc-${claimId}-${fileId}`;

	// If there's already a request in progress for this document, return that promise
	if (pendingDocumentOperations.has(cacheKey)) {
		console.log(`Reusing in-progress remove document request: ${cacheKey}`);
		return pendingDocumentOperations.get(cacheKey)!;
	}

	console.log(`API call: Remove document for claim ID: ${claimId}, file ID: ${fileId}`);

	const request: RemoveAssociatedDocumentRequest = new RemoveAssociatedDocumentRequest();
	request.setClaimId(claimId);
	request.setFileId(fileId);

	try {
		const token: string = getApiToken();
		const client: ClaimsBFFControllerClient = getBffRequest(token);

		// Create the promise and store it in the cache before awaiting results
		const removePromise = client.removeAssociatedDocument(request, {});
		pendingDocumentOperations.set(cacheKey, removePromise);

		// Return the response directly
		return await removePromise;
	} catch (err) {
		console.error("Error in removeDocument API call:", err);
		throw err;
	} finally {
		// Clean up the cache
		pendingDocumentOperations.delete(cacheKey);
	}
};

export const recoverDocument = async (claimId: number, fileId: string): Promise<RecoverAssociatedDocumentResponse> => {
	// Create a cache key based on claim and file IDs
	const cacheKey = `recover-doc-${claimId}-${fileId}`;

	// If there's already a request in progress for this document, return that promise
	if (pendingDocumentOperations.has(cacheKey)) {
		console.log(`Reusing in-progress recover document request: ${cacheKey}`);
		return pendingDocumentOperations.get(cacheKey)!;
	}

	console.log(`API call: Recover document for claim ID: ${claimId}, file ID: ${fileId}`);

	const request: RecoverAssociatedDocumentRequest = new RecoverAssociatedDocumentRequest();
	request.setClaimId(claimId);
	request.setFileId(fileId);

	try {
		// Get token
		const token: string = getApiToken();
		const client: ClaimsBFFControllerClient = getBffRequest(token);

		// Create the promise and store it in the cache before awaiting results
		const recoverPromise = client.recoverAssociatedDocument(request, {});
		pendingDocumentOperations.set(cacheKey, recoverPromise);

		// Return the response directly
		return await recoverPromise;
	} catch (err) {
		console.error("Error in recoverDocument API call:", err);
		throw err;
	} finally {
		// Clean up the cache
		pendingDocumentOperations.delete(cacheKey);
	}
};

export const permanentlyRemoveDocument = async (
	claimId: number,
	fileId: string
): Promise<PermanentlyRemoveAssociatedDocumentResponse> => {
	// Create a cache key based on claim and file IDs
	const cacheKey = `permanently-remove-doc-${claimId}-${fileId}`;

	// If there's already a request in progress for this document, return that promise
	if (pendingDocumentOperations.has(cacheKey)) {
		console.log(`Reusing in-progress permanently remove document request: ${cacheKey}`);
		return pendingDocumentOperations.get(cacheKey)!;
	}

	console.log(`API call: Permanently remove document for claim ID: ${claimId}, file ID: ${fileId}`);

	const request: PermanentlyRemoveAssociatedDocumentRequest = new PermanentlyRemoveAssociatedDocumentRequest();
	request.setClaimId(claimId);
	request.setFileId(fileId);

	try {
		// Get token
		const token: string = getApiToken();
		const client: ClaimsBFFControllerClient = getBffRequest(token);

		// Create the promise and store it in the cache before awaiting results
		const removePromise = client.permanentlyRemoveAssociatedDocument(request, {});
		pendingDocumentOperations.set(cacheKey, removePromise);

		// Return the response directly
		return await removePromise;
	} catch (err) {
		console.error("Error in permanentlyRemoveDocument API call:", err);
		throw err;
	} finally {
		// Clean up the cache
		pendingDocumentOperations.delete(cacheKey);
	}
};

export const postSubmitClaim = async (
	claimsId: number,
	receivedOn: Date | undefined
): Promise<ClaimsSubmitResponse> => {
	const token: string = getApiToken();

	const request: ClaimsSubmitRequest = new ClaimsSubmitRequest();
	request.setClaimId(claimsId);
	if (receivedOn != null) {
		request.setReceivedOn(convertDateToProtoDateTime(receivedOn));
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.submit(request, {})
		.then((response: ClaimsSubmitResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postApproveClaim = async (claimsId: number): Promise<ClaimsApproveResponse> => {
	const token: string = getApiToken();

	const request: ClaimsApproveRequest = new ClaimsApproveRequest();
	request.setId(claimsId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.approve(request, {})
		.then((response: ClaimsApproveResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postDenyClaim = async (claimsId: number, denyReason: string): Promise<ClaimsDenyResponse> => {
	const token: string = getApiToken();

	const request: ClaimsDenyRequest = new ClaimsDenyRequest();
	request.setId(claimsId);
	request.setDenyReason(denyReason);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.deny(request, {})
		.then((response: ClaimsDenyResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postInReviewClaim = async (claimId: number): Promise<ClaimsInReviewResponse> => {
	const token: string = getApiToken();

	const request: ClaimsInReviewRequest = new ClaimsInReviewRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.inReview(request, {})
		.then((response: ClaimsInReviewResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postCancelClaim = async (claimsId: number, reason: string): Promise<ClaimsCancelResponse> => {
	const token: string = getApiToken();

	const request: ClaimsCancelRequest = new ClaimsCancelRequest();
	request.setId(claimsId);
	request.setReason(reason);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.cancel(request, {})
		.then((response: ClaimsDenyResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postUncancelClaim = async (claimId: number): Promise<UncancelClaimResponse> => {
	const token: string = getApiToken();

	const request: UncancelClaimRequest = new UncancelClaimRequest();
	request.setClaimId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.uncancel(request, {})
		.then((response: UncancelClaimResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postQueueClaimForPayment = async (claimId: number): Promise<ClaimsQueueForPaymentResponse> => {
	const token: string = getApiToken();

	const request: ClaimsQueueForPaymentRequest = new ClaimsQueueForPaymentRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.queueForPayment(request, {})
		.then((response: ClaimsQueueForPaymentResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postClosePaidClaim = async (claimId: number): Promise<ClaimsClosePaidResponse> => {
	const token: string = getApiToken();

	const request: ClaimsClosePaidRequest = new ClaimsClosePaidRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.closePaid(request, {})
		.then((response: ClaimsClosePaidResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postCloseNotPaidClaim = async (claimId: number): Promise<ClaimsCloseNotPaidResponse> => {
	const token: string = getApiToken();

	const request: ClaimsCloseNotPaidRequest = new ClaimsCloseNotPaidRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.closeNotPaid(request, {})
		.then((response: ClaimsCloseNotPaidResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postCloseNotPaidGoodwillClaim = async (claimId: number): Promise<ClaimsCloseNotPaidGoodwillResponse> => {
	const token: string = getApiToken();

	const request: ClaimsCloseNotPaidGoodwillRequest = new ClaimsCloseNotPaidGoodwillRequest();
	request.setId(claimId);

	console.log("postCloseNotPaidGoodwillClaim request:", { claimId, request: request.toObject() });

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.closeNotPaidGoodwill(request, {})
		.then((response: ClaimsCloseNotPaidGoodwillResponse) => {
			console.log("postCloseNotPaidGoodwillClaim success:", response.toObject());
			return response;
		})
		.catch((err) => {
			console.error("postCloseNotPaidGoodwillClaim error:", err);
			throw err;
		});
};
export const postDuplicateClaim = async (claimId: number): Promise<ClaimsDuplicateResponse> => {
	const token: string = getApiToken();

	const request: ClaimsDuplicateRequest = new ClaimsDuplicateRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.duplicate(request, {})
		.then((response: ClaimsDuplicateResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postPendingProviderClaim = async (
	claimId: number,
	sendEmail: boolean
): Promise<ClaimsPendingProviderResponse> => {
	const token: string = getApiToken();

	const request: ClaimsPendingProviderRequest = new ClaimsPendingProviderRequest();
	request.setId(claimId);
	request.setSendEmail(sendEmail);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.pendingProvider(request, {})
		.then((response: ClaimsPendingProviderResponse) => response)
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message);
		});
};

export const postPendingVerificationClaim = async (claimId: number): Promise<ClaimsPendingVerificationResponse> => {
	const token: string = getApiToken();

	const request: ClaimsPendingVerificationRequest = new ClaimsPendingVerificationRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.pendingVerification(request, {})
		.then((response: ClaimsPendingVerificationResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postPendingInternalReviewClaim = async (claimId: number): Promise<ClaimsPendingInternalReviewResponse> => {
	const token: string = getApiToken();

	const request: ClaimsPendingInternalReviewRequest = new ClaimsPendingInternalReviewRequest();
	request.setId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.pendingInternalReview(request, {})
		.then((response: ClaimsPendingInternalReviewResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const deleteService = async (claim_id: number, service_id: number): Promise<DeleteServiceResponse> => {
	const token: string = getApiToken();

	const request: DeleteServiceRequest = new DeleteServiceRequest();
	request.setClaimId(claim_id);
	request.setClaimServiceId(service_id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.deleteService(request, {})
		.then((response: DeleteServiceResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const deleteBillings = async (claim_id: number, billing_ids: Array<number>): Promise<DeleteBillingsResponse> => {
	const token: string = getApiToken();

	const request: DeleteBillingsRequest = new DeleteBillingsRequest();
	request.setClaimId(claim_id);
	request.setBillingIdsList(billing_ids);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.deleteBillings(request, {})
		.then((response: DeleteBillingsResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getNotesByClaim = async (
	claimId: number,
	page: number = 1,
	limit: number = 10,
	filterRole?: string,
	filterNoteType?: string
): Promise<GetNotesByClaimResponse | SearchNotesResponse> => {
	const token: string = getApiToken();

	if (!claimId) {
		throw new Error("Claim ID is required");
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);

	// Use SearchNotesRequest when filters are provided
	if (filterRole || filterNoteType) {
		const searchRequest: SearchNotesRequest = new SearchNotesRequest();
		searchRequest.setClaimId(Number(claimId));
		searchRequest.setPage(Number(page));
		searchRequest.setLimit(Number(limit));

		if (filterRole) {
			searchRequest.setFilterRole(filterRole);
		}
		if (filterNoteType) {
			// Map note type label to NoteTypeName enum
			const noteTypeMap: { [key: string]: NoteTypeName } = {
				General: NoteTypeName.NOTE_TYPE_GENERAL,
				"Case Review": NoteTypeName.NOTE_TYPE_CASE_REVIEW,
				"Follow Up": NoteTypeName.NOTE_TYPE_FOLLOW_UP,
				Other: NoteTypeName.NOTE_TYPE_OTHER
			};
			const noteTypeEnum = noteTypeMap[filterNoteType];
			if (noteTypeEnum !== undefined) {
				searchRequest.setFilterNoteType(noteTypeEnum);
			}
		}

		return client
			.searchNotes(searchRequest, {})
			.then((response: SearchNotesResponse) => {
				return response;
			})
			.catch((err) => {
				console.error("Error in searchNotes:", err);
				throw err;
			});
	}

	// Use GetNotesByClaimRequest when no filters
	const request: GetNotesByClaimRequest = new GetNotesByClaimRequest();
	request.setClaimId(Number(claimId));
	request.setPage(Number(page));
	request.setLimit(Number(limit));

	return client
		.getNotesByClaim(request, {})
		.then((response: GetNotesByClaimResponse) => {
			return response;
		})
		.catch((err) => {
			console.error("Error in getNotesByClaim:", err);
			throw err;
		});
};

export const getPinnedNotesByClaim = async (claimId: number): Promise<GetPinnedNotesByClaimResponse> => {
	const token: string = getApiToken();

	if (!claimId) {
		throw new Error("Claim ID is required");
	}

	// Ensure values are numbers
	const request: GetPinnedNotesByClaimRequest = new GetPinnedNotesByClaimRequest();
	request.setClaimId(Number(claimId));

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getPinnedNotesByClaim(request, {})
		.then((response: GetPinnedNotesByClaimResponse) => {
			return response;
		})
		.catch((err) => {
			console.error("Error in getPinnedNotesByClaim:", err);
			throw err;
		});
};

export const createNote = async (
	claimId: number,
	note: string,
	noteTypeName: NoteTypeName
): Promise<CreateNoteResponse> => {
	const token: string = getApiToken();

	const request: CreateNoteRequest = new CreateNoteRequest();
	request.setClaimId(claimId);
	request.setNoteType(noteTypeName);
	request.setNote(note);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.createNote(request, {})
		.then((response: CreateNoteResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const pinNote = async (noteId: number): Promise<PinNoteResponse> => {
	const token: string = getApiToken();

	const request: PinNoteRequest = new PinNoteRequest();
	request.setNoteId(noteId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.pinNote(request, {})
		.then((response: PinNoteResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const unpinNote = async (noteId: number): Promise<UnpinNoteResponse> => {
	const token: string = getApiToken();

	const request: UnpinNoteRequest = new UnpinNoteRequest();
	request.setNoteId(noteId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.unpinNote(request, {})
		.then((response: UnpinNoteResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getApprovedClaimCountForAnalyst = async (): Promise<GetApprovedCountForAnalystResponse> => {
	const token: string = getApiToken();

	const request: GetApprovedCountForAnalystRequest = new GetApprovedCountForAnalystRequest();

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getApprovedCountForAnalyst(request, {})
		.then((response: GetApprovedCountForAnalystResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const exportClaimsToCsv = async (
	params: IParams,
	assignedAnalystId?: number
): Promise<ExportClaimsToCsvResponse> => {
	const token: string = getApiToken();

	const request = new ExportClaimsToCsvRequest();

	// Match the same logic as getClaimsList
	if (params.sortField) request.setSortField(params.sortField);
	if (params.sortDirection) request.setSortDir(params.sortDirection);
	if (params.searchField) request.setSearchField(params.searchField);
	if (params.searchString) request.setSearchString(params.searchString);
	if (assignedAnalystId) request.setAssignedAnalystId(assignedAnalystId);

	// Handle attyId if it's being used as a filter
	// Note: getClaimsList has this as a separate parameter, but for export we extract it from searchField
	if (params.searchField === "attyId" && params.searchString) {
		const attyIdNum = parseInt(params.searchString, 10);
		if (!isNaN(attyIdNum)) {
			request.setAttyId(attyIdNum);
		}
	}

	console.log("Export Claims Request:", {
		assignedAnalystId: request.hasAssignedAnalystId() ? request.getAssignedAnalystId() : "not set",
		attyId: request.hasAttyId() ? request.getAttyId() : "not set",
		searchField: request.hasSearchField() ? request.getSearchField() : "not set",
		searchString: request.hasSearchString() ? request.getSearchString() : "not set",
		sortField: request.hasSortField() ? request.getSortField() : "not set",
		sortDir: request.hasSortDir() ? request.getSortDir() : "not set"
	});

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.exportClaimsToCsv(request, {})
		.then((response: ExportClaimsToCsvResponse) => response)
		.catch((err) => {
			console.error("Error exporting claims:", err);
			console.error("Error details:", {
				code: err.code,
				message: err.message,
				metadata: err.metadata
			});
			throw new Error(err.message || "An error occurred while exporting claims");
		});
};

export const exportPaymentsToCsv = async (): Promise<ExportPaymentsToCsvResponse> => {
	const token: string = getApiToken();

	const request = new ExportPaymentsToCsvRequest();

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.exportPaymentsToCsv(request, {})
		.then((response: ExportPaymentsToCsvResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const exportSelectedClaimsToCsv = async (claimIds: Array<number>): Promise<ExportPaymentsToCsvResponse> => {
	const token: string = getApiToken();

	const request = new ExportPaymentsToCsvRequest();
	request.setClaimIdsList(claimIds);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.exportPaymentsToCsv(request, {})
		.then((response: ExportPaymentsToCsvResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getExportCsvStatus = async (): Promise<ExportPaymentsCsvStatusResponse> => {
	const token: string = getApiToken();

	const request = new ExportPaymentsCsvStatusRequest();

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.exportPaymentsCsvStatus(request, {})
		.then((response: ExportPaymentsCsvStatusResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getClaimDetailListing = async (caseId: number): Promise<GetClaimDetailListingResponse> => {
	const token: string = getApiToken();

	const request: GetClaimDetailListingRequest = new GetClaimDetailListingRequest();
	request.setCaseId(caseId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getClaimDetailListing(request, {})
		.then((response: GetClaimDetailListingResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const getIsPremiumPaid = async (claimId: number): Promise<IsPremiumPaidResponse> => {
	const token: string = getApiToken();

	const request: IsPremiumPaidRequest = new IsPremiumPaidRequest();
	request.setClaimId(claimId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.isPremiumPaid(request, {})
		.then((response: IsPremiumPaidResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postAssociateDocsChunk = async (
	claimId: number,
	bytes: Uint8Array,
	file: File,
	category: DocumentCategory,
	sequenceNumber: number,
	isLastChunk: boolean,
	uploadId?: string
): Promise<ClaimsAssociateDocsChunkResponse> => {
	const token: string = getApiToken();

	const request: ClaimsAssociateDocsChunkRequest = new ClaimsAssociateDocsChunkRequest();

	// Only include metadata in the first chunk (sequenceNumber === 1)
	const metadata = new ClaimsAssociateDocsChunkMetadata();
	metadata.setClaimId(claimId);
	metadata.setFileName(file.name);
	metadata.setMimeType(file.type);
	metadata.setCategory(category);
	metadata.setTotalSize(file.size);
	if (uploadId) {
		metadata.setId(uploadId);
	}
	request.setMetadata(metadata);
	// Set chunk data
	request.setChunkData(bytes);
	request.setSequenceNumber(sequenceNumber);
	request.setIsLastChunk(isLastChunk);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.associateDocsChunk(request, {})
		.then((response: ClaimsAssociateDocsChunkResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postUpdateClaimsDueDates = async (
	claimsDates: Array<ClaimsDueDates>
): Promise<UpdateClaimsDueDatesResponse> => {
	const token: string = getApiToken();

	const request: UpdateClaimsDueDatesRequest = new UpdateClaimsDueDatesRequest();
	request.setClaimsDueDatesList(claimsDates);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.updateClaimsDueDates(request, {})
		.then((response: UpdateClaimsDueDatesResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postUpdateClaimCleanDate = async (
	claimId: number,
	cleanDate: number
): Promise<UpdateClaimCleanDateResponse> => {
	const token: string = getApiToken();

	const request: UpdateClaimCleanDateRequest = new UpdateClaimCleanDateRequest();
	request.setClaimId(claimId);
	request.setCleanDate(cleanDate);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.updateClaimCleanDate(request, {})
		.then((response: UpdateClaimCleanDateResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Bulk update claims with assignee and/or due date
 * Only Claims Managers can execute this operation
 * @param claimIds Array of claim IDs to update
 * @param statusId Optional new status ID
 * @param assigneeId Optional new assignee ID
 * @param dueDate Optional new due date
 * @returns Promise<UpdateClaimsDueDatesResponse>
 */
export const postBulkUpdateClaims = async (
	claimIds: number[],
	statusId?: number,
	assigneeId?: number,
	dueDate?: Date
): Promise<ClaimsStatusesUpdate[] | undefined> => {
	// Validate that either assigneeId or dueDate is provided
	if (!assigneeId && !dueDate && !statusId) {
		throw new Error("Either assignee, due date, or status must be provided for bulk update");
	}

	// For now, we'll use the existing separate endpoints until the unified endpoint is implemented
	// This is a temporary implementation that will be replaced with the unified BulkUpdateClaims RPC

	// Update assignee, due date, or status if defined - we'll need to call all endpoints
	try {
		// Update assignees
		let statusUpdates: ClaimsStatusesUpdate[] | undefined = undefined;
		if (statusId) {
			statusUpdates = await postBulkUpdateClaimsStatuses(claimIds.map((id) => ({ claimId: id, statusId })));
		}

		// Update assignees
		if (assigneeId) {
			await postBulkUpdateClaimsAssignees(claimIds.map((id) => ({ claimId: id, assigneeId })));
		}

		if (dueDate) {
			// Update due dates
			const claimsDueDates = claimIds.map((claimId) => {
				const claimDueDate = new ClaimsDueDates();
				claimDueDate.setClaimId(claimId);
				claimDueDate.setDueDate(dateToInt64UTC(dueDate));
				return claimDueDate;
			});

			await postUpdateClaimsDueDates(claimsDueDates);
		}

		return statusUpdates;
	} catch (err) {
		console.error("Error in bulk update:", err);
		throw parseRpcError((err as RpcError).message || (err as Error).message).errorMessage;
	}
};

/**
 * Bulk update claims assignees
 * Only Claims Managers can execute this operation
 * @param claimsAssignees Array of claim ID and assignee ID pairs
 * @returns Promise<void> - Will be updated when backend endpoint is implemented
 */
export const postBulkUpdateClaimsAssignees = async (
	claimsAssignees: Array<{ claimId: number; assigneeId: number }>
): Promise<void> => {
	const token: string = getApiToken();
	const request = new UpdateClaimsAssigneesRequest();
	const assigneesList = claimsAssignees.map((item) => {
		const claimAssignee = new ClaimsAssignees();
		claimAssignee.setClaimId(item.claimId);
		claimAssignee.setAssigneeId(item.assigneeId);
		return claimAssignee;
	});
	request.setClaimsAssigneesList(assigneesList);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.updateClaimsAssignees(request, {})
		.then((response: UpdateClaimsAssigneesResponse) => {
			return;
		})
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message || err).errorMessage;
		});
};

/**
 * Bulk update claims statuses
 * Only Payments can execute this operation
 * @param claimsStatuses Array of claim ID and status ID pairs
 * @returns Promise<void> - Will be updated when backend endpoint is implemented
 */
export const postBulkUpdateClaimsStatuses = async (
	claimsStatuses: Array<{ claimId: number; statusId: number }>
): Promise<ClaimsStatusesUpdate[] | undefined> => {
	const token: string = getApiToken();
	const request = new UpdateClaimsStatusesRequest();
	const statusesList = claimsStatuses.map((item) => {
		const claimStatus = new ClaimsStatuses();
		claimStatus.setClaimId(item.claimId);
		claimStatus.setStatus(item.statusId);
		return claimStatus;
	});
	request.setClaimsStatusesList(statusesList);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.updateClaimsStatuses(request, {})
		.then((response: UpdateClaimsStatusesResponse) => {
			return response.getClaimsStatusesUpdatesList();
		})
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message || err).errorMessage;
		});
};

export const postClaimsStatusReasons = async (
	claimId: number,
	status: ClaimsStatus,
	reasons: (string | number)[],
	note: string | undefined
): Promise<void> => {
	const token: string = getApiToken();
	const statusReasons = reasons.map((reason) => {
		const statusReason = new ClaimsStatusReasons();
		statusReason.setClaimId(claimId);
		statusReason.setReason(+reason);
		statusReason.setStatus(status);
		if (!!note && +reason === ClaimsStatusReason.CLAIMS_STATUS_REASON_OTHER) {
			statusReason.setNote(note);
		}
		return statusReason;
	});
	const request: UpsertClaimsStatusReasonsRequest = new UpsertClaimsStatusReasonsRequest();
	request.setClaimsStatusReasonsList(statusReasons);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.upsertClaimsStatusReasons(request, {})
		.then((response: UpsertClaimsStatusReasonsResponse) => {
			return;
		})
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const checkForDuplicateClaim = async (passCaseId: number): Promise<CheckForDuplicateClaimResponse> => {
	const token: string = getApiToken();
	const request: CheckForDuplicateClaimRequest = new CheckForDuplicateClaimRequest();
	request.setPassCaseId(passCaseId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.checkForDuplicateClaim(request, {})
		.then((response: CheckForDuplicateClaimResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			throw err;
		});
};
